import React, { useState, useEffect, useCallback } from 'react';
import { VariationChain, VariationNode, SpawnStrategy } from '@/types/variationChain.types';
import { VariationTree } from './VariationTree';
import { GitBranch, Trash2, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { Button, Badge, Select } from '../ui';

interface VariationChainPanelProps {
  chain: VariationChain | null;
  tree: VariationNode[];
  onSpawn: (parentNodeId: string, count: number, strategy: SpawnStrategy) => Promise<void>;
  onRate: (nodeId: string, rating: number) => void;
  onTriggerGeneration: (node: VariationNode) => void;
  onDelete: () => void;
  isLoading: boolean;
}

export const VariationChainPanel: React.FC<VariationChainPanelProps> = ({
  chain,
  tree,
  onSpawn,
  onRate,
  onTriggerGeneration,
  onDelete,
  isLoading
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [spawnCount, setSpawnCount] = useState(3);
  const [spawnStrategy, setSpawnStrategy] = useState<SpawnStrategy>('both');
  const [evolveNodeId, setEvolveNodeId] = useState<string | null>(null);
  const [isSpawning, setIsSpawning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!chain) return null;

  const pendingNodes = chain.nodes.filter(n => n.status === 'pending');
  const generatingNodes = chain.nodes.filter(n => n.status === 'generating');
  const completeNodes = chain.nodes.filter(n => n.status === 'complete');

  const handleEvolve = (nodeId: string) => {
    setEvolveNodeId(nodeId);
  };

  const handleSpawn = async () => {
    if (!evolveNodeId) return;
    setIsSpawning(true);
    try {
      await onSpawn(evolveNodeId, spawnCount, spawnStrategy);
      setEvolveNodeId(null);
    } finally {
      setIsSpawning(false);
    }
  };

  const handleTriggerPending = () => {
    pendingNodes.forEach(node => onTriggerGeneration(node));
  };

  const strategyOptions = [
    { value: 'both', label: 'Mixed (Native + Tweak)' },
    { value: 'native', label: 'Native Variations' },
    { value: 'tweak', label: 'Tweaked Parameters' },
  ];

  const countOptions = [
    { value: '1', label: '1 variation' },
    { value: '2', label: '2 variations' },
    { value: '3', label: '3 variations' },
    { value: '4', label: '4 variations' },
    { value: '5', label: '5 variations' },
  ];

  return (
    <section className="bg-slate-900/40 rounded-3xl border-l-4 border-l-purple-500/60 shadow-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-800/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-purple-500/10 text-purple-400">
            <GitBranch size={14} />
          </div>
          <div className="text-left">
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Variation Chain
            </h3>
            <span className="text-[11px] text-slate-400">{chain.name || 'Untitled Chain'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="default-muted" size="xs" className="font-mono !text-[11px]">{chain.nodes.length} node{chain.nodes.length !== 1 ? 's' : ''}</Badge>
          {generatingNodes.length > 0 && (
            <Badge variant="warning-muted" size="xs" leftIcon={<div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />} className="!text-[11px]">
              {generatingNodes.length} generating
            </Badge>
          )}
          {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 space-y-4 animate-in slide-in-from-top-2 duration-200">
          {/* Controls */}
          <div className="flex items-center justify-between">
            {pendingNodes.length > 0 && (
              <Button
                variant="primary"
                size="xs"
                onClick={handleTriggerPending}
                leftIcon={<Zap size={10} />}
                className="!text-[11px] !font-black !uppercase"
              >
                Generate {pendingNodes.length} Pending
              </Button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {confirmDelete ? (
                <>
                  <Button variant="danger" size="xs" onClick={onDelete} className="!text-[11px] !font-black !uppercase">
                    Confirm Delete
                  </Button>
                  <Button variant="secondary" size="xs" onClick={() => setConfirmDelete(false)} className="!text-[11px]">
                    Cancel
                  </Button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-slate-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Spawn Controls (shown when evolving) */}
          {evolveNodeId && (
            <div className="bg-purple-950/10 border border-purple-900/20 rounded-2xl p-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
              <h4 className="text-[11px] font-black text-purple-400 uppercase tracking-widest">Spawn Variations</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-400 uppercase">Count</label>
                  <Select
                    value={String(spawnCount)}
                    onChange={(e) => setSpawnCount(Number(e.target.value))}
                    options={countOptions}
                    className="!bg-slate-900 !border-slate-800 !rounded-xl !text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-400 uppercase">Strategy</label>
                  <Select
                    value={spawnStrategy}
                    onChange={(e) => setSpawnStrategy(e.target.value as SpawnStrategy)}
                    options={strategyOptions}
                    className="!bg-slate-900 !border-slate-800 !rounded-xl !text-xs"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="xs"
                  onClick={handleSpawn}
                  disabled={isSpawning}
                  leftIcon={isSpawning ? <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <GitBranch size={10} />}
                  className="!text-[11px] !font-black !uppercase"
                >
                  {isSpawning ? 'Spawning...' : `Spawn ${spawnCount} Variations`}
                </Button>
                <Button variant="secondary" size="xs" onClick={() => setEvolveNodeId(null)} className="!text-[11px]">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Tree Visualization */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-slate-700 border-t-slate-400 rounded-full animate-spin" />
            </div>
          ) : (
            <VariationTree
              nodes={tree}
              onRate={onRate}
              onEvolve={handleEvolve}
            />
          )}

          {/* Stats footer */}
          <div className="flex gap-3 pt-2 border-t border-slate-800/50">
            <span className="text-[11px] text-slate-400">
              {completeNodes.length} complete
            </span>
            <span className="text-[11px] text-slate-400">
              {generatingNodes.length} generating
            </span>
            <span className="text-[11px] text-slate-400">
              {pendingNodes.length} pending
            </span>
          </div>
        </div>
      )}
    </section>
  );
};
